import React, { useCallback, Fragment, useState, useRef, memo } from 'react'
import { scrollY, stopEvent } from '@aytm/lib/common/func'
import style from '@aytm/app/swidget/questions/styles/sh/popup.module.scss'
// import Magnify from '@aytm/app/swidget/questions/components/common/magnifier'
import Magnify from '@aytm/app/swidget/base/components/Magnifier.tsx'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import '@google/model-viewer/dist/model-viewer'

interface PopupImageProps {
  src: string
  altText?: string
  magnifierClass?: string
  enableMagnify: boolean
  emulatedMobile: boolean
  volume: boolean
}

interface ImagePopup {
  width: number
  height: number
  pageX: number
  pageY: number
  magnifyOffsetX: number
  magnifyOffsetY: number
  magnifyWidth: number
  magnifyHeight: number
  magnifyX: number
  magnifyY: number
}

const ZOOM = 3
const MAGNIFIER_HEIGHT = 600
const MAGNIFIER_WIDTH = 600

export function PopupImage({ src, altText, enableMagnify, volume, emulatedMobile, magnifierClass }: PopupImageProps) {
  const { t } = useSurveyLocale()
  const refTimer = useRef<number | undefined>()
  const imageRef = useRef<HTMLImageElement>(null)

  const [show, setShow] = useState(false)
  const [imagePopup, setImagePopup] = useState<ImagePopup>({
    width: 0,
    height: 0,
    pageX: 0,
    pageY: 0,
    magnifyOffsetX: 0,
    magnifyOffsetY: 0,
    magnifyWidth: MAGNIFIER_WIDTH,
    magnifyHeight: MAGNIFIER_HEIGHT,
    magnifyX: 50,
    magnifyY: 50
  })

  const recalcMagnifierWidth = () => {
    if (imageRef.current) {
      const image = imageRef.current.getBoundingClientRect()
      // Cap magnifier to image dimensions so the cursor area never exceeds the image
      const effectiveWidth = Math.min(MAGNIFIER_WIDTH, image.width * ZOOM)
      const effectiveHeight = Math.min(MAGNIFIER_HEIGHT, image.height * ZOOM)
      const magnifyX =
        image.x + image.width / 2 + 300 + effectiveWidth < window.innerWidth
          ? image.x + image.width / 2 + 350
          : image.x + image.width + 50
      const magnifyY = image.y + image.height * 0.35 - effectiveHeight / 2
      const magnifyWidth =
        image.x + image.width + 50 + effectiveWidth > window.innerWidth
          ? window.innerWidth - image.x - image.width - 70
          : effectiveWidth

      return {
        ...imagePopup,
        magnifyX,
        magnifyY,
        magnifyWidth,
        magnifyHeight: effectiveHeight
      }
    }
    return imagePopup
  }

  const onEnterImageRef = useRef<(event: React.MouseEvent<HTMLImageElement, MouseEvent>) => void>()
  onEnterImageRef.current = event => {
    const img = event.target as HTMLImageElement
    const newPopup = recalcMagnifierWidth()
    setShow(true)
    const image = imageRef.current.getBoundingClientRect()
    setImagePopup({
      ...newPopup,
      width: img?.width || img?.offsetWidth,
      height: img?.height || img?.offsetHeight,
      pageX: Math.max(
        image.x + newPopup.magnifyWidth / ZOOM / 2,
        Math.min(event.nativeEvent.pageX, image.x + image.width - newPopup.magnifyWidth / ZOOM / 2)
      ),
      pageY: Math.max(
        image.y + newPopup.magnifyHeight / ZOOM / 2,
        Math.min(event.nativeEvent.pageY, image.y + image.height - newPopup.magnifyHeight / ZOOM / 2)
      ),
      magnifyOffsetX: Math.max(
        newPopup.magnifyWidth / ZOOM / 2,
        Math.min(event.nativeEvent.offsetX, image.width - newPopup.magnifyWidth / ZOOM / 2)
      ),
      magnifyOffsetY: Math.max(
        newPopup.magnifyHeight / ZOOM / 2,
        Math.min(event.nativeEvent.offsetY, image.height - newPopup.magnifyHeight / ZOOM / 2)
      )
    })
  }

  const onLeaveImage = () => {
    setShow(false)
  }

  const onMove = useCallback(
    (event: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
      stopEvent(event)
      clearTimeout(refTimer.current)
      if (event.nativeEvent) {
        if (show) {
          const image = imageRef.current.getBoundingClientRect()
          setImagePopup(prevPopup => {
            return {
              ...prevPopup,
              pageX: Math.max(
                image.x + prevPopup.magnifyWidth / ZOOM / 2,
                Math.min(event.nativeEvent.pageX, image.x + image.width - prevPopup.magnifyWidth / ZOOM / 2)
              ),
              pageY: Math.max(
                image.y + prevPopup.magnifyHeight / ZOOM / 2,
                Math.min(
                  event.nativeEvent.pageY - scrollY(),
                  image.y + image.height - prevPopup.magnifyHeight / ZOOM / 2
                )
              ),
              magnifyOffsetX: Math.max(
                prevPopup.magnifyWidth / ZOOM / 2,
                Math.min(event.nativeEvent.offsetX, image.width - prevPopup.magnifyWidth / ZOOM / 2)
              ),
              magnifyOffsetY: Math.max(
                prevPopup.magnifyHeight / ZOOM / 2,
                Math.min(event.nativeEvent.offsetY, image.height - prevPopup.magnifyHeight / ZOOM / 2)
              )
            }
          })
        } else {
          refTimer.current = window.setTimeout(() => {
            onEnterImageRef.current?.(event)
          }, 20)
        }
      }
    },
    [show, imagePopup.magnifyWidth, imagePopup.magnifyHeight]
  )

  const cursor = () => {
    if (!imageRef.current || !show) return null

    return (
      <div
        className="magnifierCursor"
        style={{
          top: imagePopup.pageY - imagePopup.magnifyHeight / 6 - 5,
          left: imagePopup.pageX - imagePopup.magnifyWidth / 6 - 5,
          width: imagePopup.magnifyWidth / ZOOM,
          height: imagePopup.magnifyHeight / ZOOM
        }}
      />
    )
  }

  if (volume) {
    return (
      <>
        {emulatedMobile ? (
          <div className={style.emulatedMobile}>
            <i className="icon-aytmfd-qshelf-ar"></i>
            <span>Preview on mobile device to see 3D rendering</span>
          </div>
        ) : (
          <model-viewer src={src} ar camera-controls touch-action="pan-y" />
        )}
      </>
    )
  }

  return (
    <Fragment>
      <img
        alt={altText || t('questions', 'img', 'noAlt')}
        src={src}
        onMouseEnter={onEnterImageRef.current}
        ref={imageRef}
        onMouseLeave={onLeaveImage}
        onMouseMove={onMove}
        data-pincher={!enableMagnify ? true : undefined}
      />
      {enableMagnify && (
        <Magnify
          imageSrc={src || ''}
          imageX={imagePopup.magnifyOffsetX}
          imageY={imagePopup.magnifyOffsetY}
          imageHeight={imagePopup.height || 0}
          imageWidth={imagePopup.width || 0}
          pageX={imageRef.current && imagePopup.magnifyX > 0 ? imagePopup.magnifyX : 0}
          pageY={imageRef.current && imagePopup.magnifyY > 0 ? imagePopup.magnifyY : 0}
          show={show}
          zoom={ZOOM}
          width={imagePopup.magnifyWidth}
          height={imagePopup.magnifyHeight}
          cursor={cursor()}
          fixed={true}
          externalClass={magnifierClass}
        />
      )}
    </Fragment>
  )
}

export default memo(
  PopupImage,
  (prevProps: PopupImageProps, nextProps: PopupImageProps) =>
    prevProps.src === nextProps.src &&
    prevProps.enableMagnify === nextProps.enableMagnify &&
    prevProps.altText === nextProps.altText
)
